import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { enqueue, isOfflineError } from '../../utils/offlineStore';
import { Zap, Droplets, Bug, Snowflake, ArrowUpCircle, DoorOpen, Plus, Minus, FileText, CheckCircle2, Save, FolderOpen, Trash2, ClipboardCheck, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feed types configuration
const FEED_TYPES = [
  { value: 'sugar_syrup_1_1', label: 'Sugar Syrup (1:1)' },
  { value: 'sugar_syrup_2_1', label: 'Sugar Syrup (2:1)' },
  { value: 'fondant', label: 'Fondant/Candy' },
  { value: 'pollen_patty', label: 'Pollen Patty' },
  { value: 'pollen_substitute', label: 'Pollen Substitute' },
  { value: 'honey', label: 'Honey' },
  { value: 'other', label: 'Other' }
];

const FEED_METHODS = [
  { value: 'top_feeder', label: 'Top Feeder' },
  { value: 'frame_feeder', label: 'Frame Feeder' },
  { value: 'entrance_feeder', label: 'Entrance Feeder' },
  { value: 'baggie_feeder', label: 'Baggie Feeder' },
  { value: 'open_feeding', label: 'Open Feeding' },
  { value: 'other', label: 'Other' }
];

// Treatment types
const TREATMENT_TYPES = [
  { value: 'oxalic_acid_vapor', label: 'Oxalic Acid (Vaporization)' },
  { value: 'oxalic_acid_dribble', label: 'Oxalic Acid (Dribble)' },
  { value: 'formic_acid', label: 'Formic Acid' },
  { value: 'apistan', label: 'Apistan Strips' },
  { value: 'apivar', label: 'Apivar Strips' },
  { value: 'hopguard', label: 'HopGuard' },
  { value: 'thymol', label: 'Thymol (Apiguard/ApiLife)' },
  { value: 'mite_away', label: 'Mite Away Quick Strips' },
  { value: 'powdered_sugar', label: 'Powdered Sugar Dusting' },
  { value: 'other', label: 'Other Treatment' }
];

const TREATMENT_FOR = [
  { value: 'varroa', label: 'Varroa Mites' },
  { value: 'nosema', label: 'Nosema' },
  { value: 'american_foulbrood', label: 'American Foulbrood' },
  { value: 'european_foulbrood', label: 'European Foulbrood' },
  { value: 'small_hive_beetle', label: 'Small Hive Beetle' },
  { value: 'wax_moth', label: 'Wax Moth' },
  { value: 'chalkbrood', label: 'Chalkbrood' },
  { value: 'preventative', label: 'Preventative' },
  { value: 'other', label: 'Other' }
];

// Super configurations
const SUPER_TYPES = [
  { value: 'deep', label: 'Deep Super' },
  { value: 'medium', label: 'Medium Super' },
  { value: 'shallow', label: 'Shallow/Ideal Super' }
];

const FRAME_SIZES = ['8', '10'];

const BulkYardActionDialog = ({ open, onOpenChange, yardName, hives = [], onSuccess }) => {
  const [selectedAction, setSelectedAction] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  
  // Crew members for assignment
  const [crewMembers, setCrewMembers] = useState([]);
  const [loadingCrew, setLoadingCrew] = useState(false);
  
  // Feed form state
  const [feedData, setFeedData] = useState({
    feed_type: '',
    feed_type_other: '',
    quantity: '',
    unit: 'liters',
    method: '',
    method_other: '',
    notes: ''
  });
  
  // Treatment form state
  const [treatmentData, setTreatmentData] = useState({
    treatment_type: '',
    treatment_type_other: '',
    treatment_for: '',
    treatment_for_other: '',
    dosage: '',
    dosage_unit: 'ml',
    application_method: '',
    follow_up_date: '',
    notes: ''
  });
  
  // Super form state
  const [superData, setSuperData] = useState({
    action: 'add', // 'add' or 'remove'
    super_type: 'medium',
    frame_size: '10',
    quantity: 1,
    notes: ''
  });
  
  // Winterize form state
  const [winterizeData, setWinterizeData] = useState({
    insulation_added: true,
    entrance_reducer: true,
    mouse_guard: true,
    moisture_board: false,
    candy_board: false,
    notes: ''
  });
  
  // Generic action form state
  const [genericData, setGenericData] = useState({
    action_type: '',
    notes: ''
  });

  // Inspect All form state
  const [inspectData, setInspectData] = useState({
    inspection_type: 'quick',
    notes: ''
  });

  // Assign Crew form state - supports multiple crew members
  const [assignData, setAssignData] = useState({
    selected_crew: [], // Array of crew member IDs
    task_type: 'inspection',
    custom_task_type: '', // For custom task type
    priority: 'normal',
    due_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Custom action form state
  const [customData, setCustomData] = useState({
    action_name: '',
    description: '',
    notes: ''
  });

  // Template state
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Fetch templates on mount and when action changes
  useEffect(() => {
    if (open && selectedAction) {
      fetchTemplates(selectedAction);
    }
  }, [open, selectedAction]);

  // Fetch crew members when dialog opens
  useEffect(() => {
    if (open) {
      fetchCrewMembers();
    }
  }, [open]);

  const fetchCrewMembers = async () => {
    setLoadingCrew(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crew/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrewMembers(response.data || []);
    } catch (error) {
      setCrewMembers([]);
    } finally {
      setLoadingCrew(false);
    }
  };

  const fetchTemplates = async (actionType) => {
    setLoadingTemplates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bulk-action-templates?action_type=${actionType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data || []);
    } catch (error) {
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    let templateData = {};
    switch (selectedAction) {
      case 'feed':
        templateData = { ...feedData };
        break;
      case 'treat':
        templateData = { ...treatmentData };
        break;
      case 'winterize':
        templateData = { ...winterizeData };
        break;
      case 'add_super':
        templateData = { ...superData };
        break;
      default:
        templateData = { ...genericData };
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bulk-action-templates`, {
        name: templateName.trim(),
        action_type: selectedAction,
        template_data: templateData
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`Template "${templateName}" saved!`);
      setShowSaveTemplate(false);
      setTemplateName('');
      fetchTemplates(selectedAction);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const loadTemplate = (template) => {
    const data = template.template_data;
    switch (selectedAction) {
      case 'feed':
        setFeedData({ ...feedData, ...data });
        break;
      case 'treat':
        setTreatmentData({ ...treatmentData, ...data });
        break;
      case 'winterize':
        setWinterizeData({ ...winterizeData, ...data });
        break;
      case 'add_super':
        setSuperData({ ...superData, ...data });
        break;
      default:
        setGenericData({ ...genericData, ...data });
    }
    toast.success(`Loaded template: ${template.name}`);
  };

  const deleteTemplate = async (templateId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this template?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/bulk-action-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchTemplates(selectedAction);
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const BULK_ACTIONS = [
    { id: 'assign_crew', label: 'Assign to Crew', icon: Users, color: 'bg-indigo-500 hover:bg-indigo-600', description: 'Assign tasks to crew members' },
    { id: 'inspect', label: 'Inspect All', icon: ClipboardCheck, color: 'bg-green-500 hover:bg-green-600', description: 'Record inspection for all hives' },
    { id: 'feed', label: 'Feed All', icon: Droplets, color: 'bg-blue-500 hover:bg-blue-600', description: 'Feed all hives in this yard' },
    { id: 'treat', label: 'Treat All', icon: Bug, color: 'bg-red-500 hover:bg-red-600', description: 'Apply treatment to all hives' },
    { id: 'winterize', label: 'Winterize All', icon: Snowflake, color: 'bg-cyan-500 hover:bg-cyan-600', description: 'Prepare all hives for winter' },
    { id: 'add_super', label: 'Add/Remove Supers', icon: ArrowUpCircle, color: 'bg-amber-500 hover:bg-amber-600', description: 'Add or remove supers from all hives' },
    { id: 'bee_escape', label: 'Bee Escapes', icon: DoorOpen, color: 'bg-purple-500 hover:bg-purple-600', description: 'Add bee escapes to all hives' },
    { id: 'note', label: 'Add Note', icon: FileText, color: 'bg-stone-500 hover:bg-stone-600', description: 'Add a note to all hives' },
    { id: 'custom', label: 'Custom Action', icon: Plus, color: 'bg-gray-500 hover:bg-gray-600', description: 'Create your own custom action' }
  ];

  // Task types for crew assignment - includes all bulk actions
  const CREW_TASK_TYPES = [
    { value: 'inspection', label: 'Inspection' },
    { value: 'feeding', label: 'Feeding' },
    { value: 'treatment', label: 'Treatment' },
    { value: 'winterize', label: 'Winterization' },
    { value: 'add_super', label: 'Add/Remove Supers' },
    { value: 'bee_escape', label: 'Bee Escapes' },
    { value: 'harvest', label: 'Harvest' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'queen_check', label: 'Queen Check' },
    { value: 'swarm_prevention', label: 'Swarm Prevention' },
    { value: 'split', label: 'Split Hive' },
    { value: 'combine', label: 'Combine Hives' },
    { value: 'requeen', label: 'Requeen' },
    { value: 'mite_count', label: 'Mite Count' },
    { value: 'custom', label: 'Custom Task' }
  ];

  const resetForms = () => {
    setSelectedAction(null);
    setCompletedCount(0);
    setFeedData({ feed_type: '', feed_type_other: '', quantity: '', unit: 'liters', method: '', method_other: '', notes: '' });
    setTreatmentData({ treatment_type: '', treatment_type_other: '', treatment_for: '', treatment_for_other: '', dosage: '', dosage_unit: 'ml', application_method: '', follow_up_date: '', notes: '' });
    setSuperData({ action: 'add', super_type: 'medium', frame_size: '10', quantity: 1, notes: '' });
    setWinterizeData({ insulation_added: true, entrance_reducer: true, mouse_guard: true, moisture_board: false, candy_board: false, notes: '' });
    setGenericData({ action_type: '', notes: '' });
    setInspectData({ inspection_type: 'quick', notes: '' });
    setAssignData({ selected_crew: [], task_type: 'inspection', custom_task_type: '', priority: 'normal', due_date: new Date().toISOString().split('T')[0], notes: '' });
    setCustomData({ action_name: '', description: '', notes: '' });
  };

  const handleClose = () => {
    resetForms();
    onOpenChange(false);
  };

  const submitBulkAction = async () => {
    if (!selectedAction) return;
    
    setIsSubmitting(true);
    setCompletedCount(0);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build action data based on selected action
      let actionData = {};
      let actionType = '';
      
      switch (selectedAction) {
        case 'inspect':
          actionType = 'inspection';
          actionData = {
            inspection_type: inspectData.inspection_type,
            notes: inspectData.notes || `Bulk inspection for ${yardName}`
          };
          break;
          
        case 'assign_crew':
          // Handle crew assignment separately
          actionType = 'crew_assignment';
          actionData = {
            crew_member_id: assignData.crew_member_id,
            task_type: assignData.task_type,
            priority: assignData.priority,
            due_date: assignData.due_date,
            notes: assignData.notes
          };
          break;
          
        case 'feed':
          actionType = 'feeding';
          actionData = {
            feeding_details: {
              feed_type: feedData.feed_type === 'other' ? feedData.feed_type_other : feedData.feed_type,
              quantity: feedData.quantity,
              unit: feedData.unit,
              method: feedData.method === 'other' ? feedData.method_other : feedData.method
            },
            notes: feedData.notes || `Bulk feeding: ${feedData.feed_type === 'other' ? feedData.feed_type_other : FEED_TYPES.find(f => f.value === feedData.feed_type)?.label || feedData.feed_type}`
          };
          break;
          
        case 'treat':
          actionType = 'treatment';
          actionData = {
            treatment_details: {
              treatment_type: treatmentData.treatment_type === 'other' ? treatmentData.treatment_type_other : treatmentData.treatment_type,
              treatment_for: treatmentData.treatment_for === 'other' ? treatmentData.treatment_for_other : treatmentData.treatment_for,
              dosage: treatmentData.dosage,
              dosage_unit: treatmentData.dosage_unit,
              follow_up_date: treatmentData.follow_up_date
            },
            notes: treatmentData.notes || `Bulk treatment: ${TREATMENT_TYPES.find(t => t.value === treatmentData.treatment_type)?.label || treatmentData.treatment_type}`
          };
          break;
          
        case 'winterize':
          actionType = 'winterization';
          actionData = {
            winterization_details: winterizeData,
            notes: winterizeData.notes || 'Bulk winterization applied'
          };
          break;
          
        case 'add_super':
          actionType = superData.action === 'add' ? 'super_added' : 'super_removed';
          actionData = {
            super_details: {
              action: superData.action,
              super_type: superData.super_type,
              frame_size: superData.frame_size,
              quantity: superData.quantity
            },
            notes: superData.notes || `Bulk ${superData.action === 'add' ? 'added' : 'removed'} ${superData.quantity} ${superData.super_type} super(s)`
          };
          break;
          
        case 'bee_escape':
          actionType = 'bee_escape';
          actionData = {
            bee_escape_details: { added: true },
            notes: genericData.notes || 'Bee escape added'
          };
          break;
          
        case 'note':
          actionType = 'note';
          actionData = {
            notes: genericData.notes
          };
          break;
          
        case 'custom':
          // Handled separately below
          break;
          
        case 'assign_crew':
          // Handled separately below
          break;
          
        default:
          throw new Error('Unknown action type');
      }

      // Submit action for each hive
      let successCount = 0;
      const errors = [];
      
      // Handle crew assignment separately - supports multiple crew members
      if (selectedAction === 'assign_crew') {
        const selectedCrewNames = assignData.selected_crew
          .map(id => crewMembers.find(c => c.id === id)?.name)
          .filter(Boolean);
        
        const taskTypeLabel = assignData.task_type === 'custom' 
          ? assignData.custom_task_type 
          : CREW_TASK_TYPES.find(t => t.value === assignData.task_type)?.label || assignData.task_type;
        
        for (let i = 0; i < hives.length; i++) {
          const hive = hives[i];
          try {
            await axios.post(`${API}/crew/tasks`, {
              assigned_crew: assignData.selected_crew, // Multiple crew IDs
              hive_id: hive.id,
              hive_number: hive.hive_number,
              yard: yardName,
              title: `${taskTypeLabel}: Hive #${hive.hive_number}`,
              description: assignData.notes || `Bulk assigned task for ${yardName}`,
              task_type: assignData.task_type === 'custom' ? assignData.custom_task_type : assignData.task_type,
              priority: assignData.priority,
              due_date: assignData.due_date
            }, { headers });
            successCount++;
            setCompletedCount(successCount);
          } catch (err) {
            errors.push(`Hive ${hive.hive_number}: ${err.response?.data?.detail || err.message}`);
          }
        }
        
        const crewNamesStr = selectedCrewNames.length > 2 
          ? `${selectedCrewNames.slice(0, 2).join(', ')} +${selectedCrewNames.length - 2} more`
          : selectedCrewNames.join(' & ');
        
        if (successCount === hives.length) {
          toast.success(`${successCount} tasks assigned to ${crewNamesStr} in ${yardName}!`);
        } else if (successCount > 0) {
          toast.warning(`${successCount}/${hives.length} tasks assigned. ${errors.length} errors.`);
        } else {
          toast.error('Failed to assign any tasks');
        }
        
        if (successCount > 0) {
          onSuccess?.();
          handleClose();
        }
        return;
      }
      
      // Handle custom action
      if (selectedAction === 'custom') {
        for (let i = 0; i < hives.length; i++) {
          const hive = hives[i];
          try {
            const inspectionData = {
              hive_id: hive.id,
              date: new Date().toISOString(),
              inspection_type: ['quick'],
              actions: [customData.action_name || 'custom_action'],
              notes: `${customData.action_name}: ${customData.description}\n${customData.notes}`.trim(),
              bulk_action: true,
              bulk_yard: yardName,
              custom_action: true,
              custom_action_name: customData.action_name
            };
            
            await axios.post(`${API}/inspections`, inspectionData, { headers });
            successCount++;
            setCompletedCount(successCount);
          } catch (err) {
            errors.push(`Hive ${hive.hive_number}: ${err.response?.data?.detail || err.message}`);
          }
        }
        
        if (successCount === hives.length) {
          toast.success(`${customData.action_name || 'Custom action'} applied to ${successCount} hives!`);
        } else if (successCount > 0) {
          toast.warning(`${successCount}/${hives.length} completed. ${errors.length} errors.`);
        } else {
          toast.error('Failed to apply custom action');
        }
        
        if (successCount > 0) {
          onSuccess?.();
          handleClose();
        }
        return;
      }
      
      const buildBulkInspection = (hive) => ({
            hive_id: hive.id,
            date: new Date().toISOString(),
            inspection_type: selectedAction === 'inspect' ? [actionData.inspection_type] : ['quick'],
            actions: [actionType],
            notes: actionData.notes || `Bulk ${actionType} action`,
            bulk_action: true,
            bulk_yard: yardName,
            // Include specific action data in relevant fields
            ...(selectedAction === 'inspect' && {
              type_of_inspection: actionData.inspection_type === 'full' ? 'Full Inspection' : 'Quick Check'
            }),
            ...(selectedAction === 'feed' && {
              feeding: 'yes',
              feed_type: actionData.feeding_details?.feed_type ? [actionData.feeding_details.feed_type] : [],
              feeder_type: actionData.feeding_details?.method || ''
            }),
            ...(selectedAction === 'treat' && {
              mite_treatment: actionData.treatment_details?.treatment_type ? [actionData.treatment_details.treatment_type] : [],
              mite_dosage: actionData.treatment_details?.dosage || ''
            }),
            ...(selectedAction === 'bee_escape' && {
              actions: ['bee_escape_added']
            }),
            ...(selectedAction === 'add_super' && {
              actions: [actionData.super_details?.action === 'add' ? 'super_added' : 'super_removed']
            }),
            ...(selectedAction === 'winterize' && {
              actions: ['winterized']
            })
          });

      // No signal: keep the whole bulk feed/treat on the phone and replay it later
      const queueBulkOffline = async () => {
        const followUps = [
          ...hives.slice(1).map(h => ({ method: 'post', path: '/inspections', body: buildBulkInspection(h) })),
          { method: 'post', path: '/yard-actions', body: { yard_name: yardName, action_type: actionType, action_data: actionData, hive_count: hives.length, hive_ids: hives.map(h => h.id), date: new Date().toISOString() } },
        ];
        if (selectedAction === 'treat' && actionData.treatment_details?.treatment_type) {
          followUps.push({ method: 'post', path: '/treatment-plans/bulk', body: {
            treatment_type_name: actionData.treatment_details.treatment_type, dosage: actionData.treatment_details.dosage || '',
            hive_ids: hives.map(h => h.id), hive_numbers: hives.map(h => String(h.hive_number)), yard_name: yardName,
            notes: actionData.notes || `Bulk treatment applied to ${hives.length} hives in ${yardName}`, date: new Date().toISOString() } });
        }
        await enqueue({ kind: selectedAction === 'feed' ? 'feeding' : 'treatment', path: '/inspections', body: buildBulkInspection(hives[0]),
          label: `${selectedAction === 'feed' ? 'Feeding' : 'Treatment'} · ${yardName} (${hives.length} hives)`, hiveIds: hives.map(h => h.id), followUps });
        toast.success(`No signal: ${selectedAction === 'feed' ? 'feeding' : 'treatment'} for ${hives.length} hives saved on this phone`, { description: 'It will upload automatically when you are back in coverage.', duration: 6000 });
        onSuccess?.();
        handleClose();
      };
      const canQueue = ['feed', 'treat'].includes(selectedAction) && hives.length > 0;
      if (canQueue && !navigator.onLine) { await queueBulkOffline(); return; }

      for (let i = 0; i < hives.length; i++) {
        const hive = hives[i];
        try {
          // Create an inspection record with the action
          const inspectionData = buildBulkInspection(hive);
          
          await axios.post(`${API}/inspections`, inspectionData, { headers });
          successCount++;
          setCompletedCount(successCount);
        } catch (err) {
          if (i === 0 && canQueue && isOfflineError(err)) { await queueBulkOffline(); return; }
          errors.push(`Hive ${hive.hive_number}: ${err.response?.data?.detail || err.message}`);
        }
      }

      // Also record to yard history
      try {
        await axios.post(`${API}/yard-actions`, {
          yard_name: yardName,
          action_type: actionType,
          action_data: actionData,
          hive_count: successCount,
          hive_ids: hives.filter((_, i) => i < successCount).map(h => h.id),
          date: new Date().toISOString()
        }, { headers });
      } catch (err) {
      }

      // If this was a bulk treatment, also create a treatment plan for Mite Control tracking
      if (selectedAction === 'treat' && successCount > 0 && actionData.treatment_details?.treatment_type) {
        try {
          const treatedHives = hives.filter((_, i) => i < successCount);
          await axios.post(`${API}/treatment-plans/bulk`, {
            treatment_type_name: actionData.treatment_details.treatment_type,
            dosage: actionData.treatment_details.dosage || '',
            hive_ids: treatedHives.map(h => h.id),
            hive_numbers: treatedHives.map(h => String(h.hive_number)),
            yard_name: yardName,
            notes: actionData.notes || `Bulk treatment applied to ${successCount} hives in ${yardName}`,
            date: new Date().toISOString()
          }, { headers });
        } catch (err) {
          console.warn('Could not create treatment plan record:', err);
        }
      }

      if (successCount === hives.length) {
        toast.success(`${selectedAction === 'feed' ? 'Fed' : selectedAction === 'treat' ? 'Treated' : 'Action applied to'} all ${successCount} hives in ${yardName}!`);
      } else if (successCount > 0) {
        toast.warning(`Action applied to ${successCount}/${hives.length} hives. ${errors.length} errors.`);
      } else {
        toast.error('Failed to apply action to any hives');
      }

      if (successCount > 0) {
        onSuccess?.();
        handleClose();
      }
    } catch (error) {
      toast.error('Failed to apply bulk action');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderActionForm = () => {
    if (!selectedAction) return null;

    switch (selectedAction) {
      case 'inspect':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-600">Record a bulk inspection for all {hives.length} hives in {yardName}.</p>
            <div className="space-y-2">
              <Label>Inspection Type</Label>
              <Select value={inspectData.inspection_type} onValueChange={(v) => setInspectData({...inspectData, inspection_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quick">Quick Check</SelectItem>
                  <SelectItem value="full">Full Inspection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Bulk inspection notes..."
                value={inspectData.notes}
                onChange={(e) => setInspectData({...inspectData, notes: e.target.value})}
              />
            </div>
          </div>
        );

      case 'assign_crew':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-600">Assign tasks for all {hives.length} hives in {yardName} to crew members.</p>
            
            {/* Multiple Crew Selection */}
            <div className="space-y-2">
              <Label>Assign To * (select multiple)</Label>
              <div className="border border-stone-200 rounded-lg p-3 max-h-[150px] overflow-y-auto bg-white">
                {loadingCrew ? (
                  <p className="text-sm text-stone-500">Loading crew...</p>
                ) : crewMembers.length === 0 ? (
                  <p className="text-sm text-amber-600">No crew members. Add crew members in Crew Management first.</p>
                ) : (
                  <div className="space-y-2">
                    {crewMembers.map(m => (
                      <label key={m.id} className="flex items-center gap-2 cursor-pointer hover:bg-stone-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={assignData.selected_crew.includes(m.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAssignData({...assignData, selected_crew: [...assignData.selected_crew, m.id]});
                            } else {
                              setAssignData({...assignData, selected_crew: assignData.selected_crew.filter(id => id !== m.id)});
                            }
                          }}
                          className="w-4 h-4 rounded border-stone-300 text-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="text-sm font-medium">{m.name}</span>
                        {m.email && <span className="text-xs text-stone-400">({m.email})</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {assignData.selected_crew.length > 0 && (
                <p className="text-xs text-indigo-600 font-medium">{assignData.selected_crew.length} crew member(s) selected</p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Task Type</Label>
                <Select value={assignData.task_type} onValueChange={(v) => setAssignData({...assignData, task_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CREW_TASK_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {assignData.task_type === 'custom' && (
                  <Input
                    placeholder="Enter custom task type..."
                    value={assignData.custom_task_type}
                    onChange={(e) => setAssignData({...assignData, custom_task_type: e.target.value})}
                    className="mt-2"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={assignData.priority} onValueChange={(v) => setAssignData({...assignData, priority: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input
                type="date"
                value={assignData.due_date}
                onChange={(e) => setAssignData({...assignData, due_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes/Instructions (optional)</Label>
              <Textarea
                placeholder="Instructions for crew members..."
                value={assignData.notes}
                onChange={(e) => setAssignData({...assignData, notes: e.target.value})}
              />
            </div>
            
            {assignData.selected_crew.length > 1 && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-700">
                <strong>Note:</strong> All {assignData.selected_crew.length} selected crew members will receive tasks for {hives.length} hives. 
                Each crew member will see who else is assigned on their dashboard.
              </div>
            )}
          </div>
        );

      case 'feed':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Feed Type *</Label>
                <Select value={feedData.feed_type} onValueChange={(v) => setFeedData({...feedData, feed_type: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select feed type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FEED_TYPES.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {feedData.feed_type === 'other' && (
                  <Input
                    placeholder="Specify feed type"
                    value={feedData.feed_type_other}
                    onChange={(e) => setFeedData({...feedData, feed_type_other: e.target.value})}
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Method</Label>
                <Select value={feedData.method} onValueChange={(v) => setFeedData({...feedData, method: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {FEED_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity per Hive</Label>
                <Input
                  type="number"
                  placeholder="Amount"
                  value={feedData.quantity}
                  onChange={(e) => setFeedData({...feedData, quantity: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={feedData.unit} onValueChange={(v) => setFeedData({...feedData, unit: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="liters">Liters</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="lbs">lbs</SelectItem>
                    <SelectItem value="cups">Cups</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Any additional notes..."
                value={feedData.notes}
                onChange={(e) => setFeedData({...feedData, notes: e.target.value})}
              />
            </div>
          </div>
        );

      case 'treat':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Treatment Type *</Label>
                <Select value={treatmentData.treatment_type} onValueChange={(v) => setTreatmentData({...treatmentData, treatment_type: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select treatment" />
                  </SelectTrigger>
                  <SelectContent>
                    {TREATMENT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {treatmentData.treatment_type === 'other' && (
                  <Input
                    placeholder="Specify treatment"
                    value={treatmentData.treatment_type_other}
                    onChange={(e) => setTreatmentData({...treatmentData, treatment_type_other: e.target.value})}
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Treating For</Label>
                <Select value={treatmentData.treatment_for} onValueChange={(v) => setTreatmentData({...treatmentData, treatment_for: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    {TREATMENT_FOR.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dosage per Hive</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Amount"
                    value={treatmentData.dosage}
                    onChange={(e) => setTreatmentData({...treatmentData, dosage: e.target.value})}
                    className="flex-1"
                  />
                  <Select value={treatmentData.dosage_unit} onValueChange={(v) => setTreatmentData({...treatmentData, dosage_unit: v})}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="g">g</SelectItem>
                      <SelectItem value="strips">strips</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Follow-up Date</Label>
                <Input
                  type="date"
                  value={treatmentData.follow_up_date}
                  onChange={(e) => setTreatmentData({...treatmentData, follow_up_date: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Treatment notes..."
                value={treatmentData.notes}
                onChange={(e) => setTreatmentData({...treatmentData, notes: e.target.value})}
              />
            </div>
          </div>
        );

      case 'winterize':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-600">Select winterization steps to apply to all hives:</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'insulation_added', label: 'Add Insulation' },
                { key: 'entrance_reducer', label: 'Entrance Reducer' },
                { key: 'mouse_guard', label: 'Mouse Guard' },
                { key: 'moisture_board', label: 'Moisture Board' },
                { key: 'candy_board', label: 'Candy Board/Emergency Feed' }
              ].map(item => (
                <label key={item.key} className="flex items-center gap-2 p-3 bg-stone-50 rounded-lg cursor-pointer hover:bg-stone-100">
                  <input
                    type="checkbox"
                    checked={winterizeData[item.key]}
                    onChange={(e) => setWinterizeData({...winterizeData, [item.key]: e.target.checked})}
                    className="rounded border-stone-300"
                  />
                  <span className="text-sm">{item.label}</span>
                </label>
              ))}
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Winterization notes..."
                value={winterizeData.notes}
                onChange={(e) => setWinterizeData({...winterizeData, notes: e.target.value})}
              />
            </div>
          </div>
        );

      case 'add_super':
        return (
          <div className="space-y-4">
            <div className="flex gap-2 mb-4">
              <Button
                type="button"
                variant={superData.action === 'add' ? 'default' : 'outline'}
                onClick={() => setSuperData({...superData, action: 'add'})}
                className={superData.action === 'add' ? 'bg-green-500 hover:bg-green-600' : ''}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Super
              </Button>
              <Button
                type="button"
                variant={superData.action === 'remove' ? 'default' : 'outline'}
                onClick={() => setSuperData({...superData, action: 'remove'})}
                className={superData.action === 'remove' ? 'bg-red-500 hover:bg-red-600' : ''}
              >
                <Minus className="h-4 w-4 mr-1" /> Remove Super
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Super Type</Label>
                <Select value={superData.super_type} onValueChange={(v) => setSuperData({...superData, super_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPER_TYPES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Frame Size</Label>
                <Select value={superData.frame_size} onValueChange={(v) => setSuperData({...superData, frame_size: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FRAME_SIZES.map(s => (
                      <SelectItem key={s} value={s}>{s} Frames</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Select value={String(superData.quantity)} onValueChange={(v) => setSuperData({...superData, quantity: parseInt(v)})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4].map(n => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Notes..."
                value={superData.notes}
                onChange={(e) => setSuperData({...superData, notes: e.target.value})}
              />
            </div>
          </div>
        );

      case 'bee_escape':
      case 'note':
        return (
          <div className="space-y-4">
            {selectedAction === 'bee_escape' && (
              <p className="text-sm text-stone-600">This will record bee escapes added to all {hives.length} hives in {yardName}.</p>
            )}
            <div className="space-y-2">
              <Label>{selectedAction === 'note' ? 'Note for all hives *' : 'Notes'}</Label>
              <Textarea
                placeholder={selectedAction === 'note' ? 'Enter note to add to all hives...' : 'Any additional notes...'}
                value={genericData.notes}
                onChange={(e) => setGenericData({...genericData, notes: e.target.value})}
                rows={4}
              />
            </div>
          </div>
        );

      case 'custom':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-600">Create a custom action to apply to all {hives.length} hives in {yardName}.</p>
            <div className="space-y-2">
              <Label>Action Name *</Label>
              <Input
                placeholder="e.g., Check Entrance Reducer, Rotate Boxes, Check Water Source..."
                value={customData.action_name}
                onChange={(e) => setCustomData({...customData, action_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Brief description of the action..."
                value={customData.description}
                onChange={(e) => setCustomData({...customData, description: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any additional notes to record with this action..."
                value={customData.notes}
                onChange={(e) => setCustomData({...customData, notes: e.target.value})}
                rows={3}
              />
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
              <strong>Tip:</strong> Custom actions are great for yard-specific tasks like checking equipment, 
              observing foraging patterns, or any other action not covered by the standard options.
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canSubmit = () => {
    if (!selectedAction) return false;
    switch (selectedAction) {
      case 'inspect': return true;
      case 'assign_crew': return assignData.selected_crew.length > 0;
      case 'feed': return !!feedData.feed_type;
      case 'treat': return !!treatmentData.treatment_type;
      case 'winterize': return Object.values(winterizeData).some(v => v === true);
      case 'add_super': return true;
      case 'bee_escape': return true;
      case 'note': return !!genericData.notes.trim();
      case 'custom': return !!customData.action_name.trim();
      default: return false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-purple-500" />
            Bulk Action - {yardName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Hive count info */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-800">
              This action will apply to <span className="font-bold">{hives.length} hive{hives.length !== 1 ? 's' : ''}</span> in <span className="font-bold">{yardName}</span>
            </p>
          </div>

          {/* Action selector */}
          {!selectedAction ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {BULK_ACTIONS.map(action => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => setSelectedAction(action.id)}
                    className={`${action.color} text-white p-4 rounded-xl text-left transition-all hover:scale-105 hover:shadow-lg`}
                    data-testid={`bulk-action-${action.id}`}
                  >
                    <Icon className="h-6 w-6 mb-2" />
                    <div className="font-semibold">{action.label}</div>
                    <div className="text-xs opacity-90 mt-1">{action.description}</div>
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              {/* Back button and action title */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedAction(null)}>
                    ← Back
                  </Button>
                  <span className="font-semibold">
                    {BULK_ACTIONS.find(a => a.id === selectedAction)?.label}
                  </span>
                </div>
              </div>

              {/* Template Section - Load/Save */}
              {(selectedAction === 'feed' || selectedAction === 'treat' || selectedAction === 'winterize') && (
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-3">
                    {/* Load Template */}
                    <div className="flex items-center gap-2 flex-1">
                      <FolderOpen className="h-4 w-4 text-stone-500" />
                      <Select 
                        value="" 
                        onValueChange={(templateId) => {
                          const template = templates.find(t => t.id === templateId);
                          if (template) loadTemplate(template);
                        }}
                      >
                        <SelectTrigger className="h-9 bg-white">
                          <SelectValue placeholder={loadingTemplates ? "Loading..." : templates.length ? "Load Template" : "No templates saved"} />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(t => (
                            <SelectItem key={t.id} value={t.id}>
                              <div className="flex items-center justify-between w-full">
                                <span>{t.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {templates.length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            const selectedTemplate = templates[0]; // For delete, user can manage via select
                            if (selectedTemplate) deleteTemplate(selectedTemplate.id, { stopPropagation: () => {} });
                          }}
                          className="h-9 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Delete selected template"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Save Template */}
                    {!showSaveTemplate ? (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowSaveTemplate(true)}
                        className="h-9"
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Save as Template
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Template name..."
                          value={templateName}
                          onChange={(e) => setTemplateName(e.target.value)}
                          className="h-9 w-40"
                          onKeyDown={(e) => e.key === 'Enter' && saveTemplate()}
                        />
                        <Button size="sm" onClick={saveTemplate} className="h-9 bg-green-500 hover:bg-green-600">
                          Save
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }} className="h-9">
                          ✕
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action form */}
              {renderActionForm()}

              {/* Progress indicator */}
              {isSubmitting && (
                <div className="bg-stone-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Processing...</span>
                    <span className="text-sm text-stone-600">{completedCount}/{hives.length}</span>
                  </div>
                  <div className="w-full bg-stone-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(completedCount / hives.length) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Submit button */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button 
                  onClick={submitBulkAction}
                  disabled={!canSubmit() || isSubmitting}
                  className="bg-purple-500 hover:bg-purple-600"
                  data-testid="submit-bulk-action"
                >
                  {isSubmitting ? (
                    <>Processing... ({completedCount}/{hives.length})</>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Apply to All {hives.length} Hives
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkYardActionDialog;
